import { Fragment, ReactElement, useEffect, useState, useRef } from 'react';
import { nanoid } from 'nanoid';
import {
	DataTable as RMWCDataTable,
	SimpleDataTable,
	DataTableContent,
	DataTableHead,
	DataTableHeadCell,
	DataTableBody,
	DataTableRow,
	DataTableCell,
} from '@rmwc/data-table';
import { Button, Checkbox, IconButton } from '@upmc/ims.component-library';
import TextField from '../TextField/TextField';
import ChipSet from '../../Chips/ChipSet/ChipSet';
import Chip from '../../Chips/Chip/Chip';
import Icon from '../Icon/Icon';
import { Headline6, Subtitle2 } from '../Typography/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import { Tooltip } from '../Tooltip/Tooltip';
import { CONTENT_ALIGNMENT } from '../Tooltip/_types';
import { iconLegend as IconLegend } from '../Icon/IconLegend/IconLegend';
import StyleAuxiliary from '../../../../hoc/Auxiliary/StyleAuxiliary/StyleAuxiliary';
import classnames from 'classnames';
import { getActionCellMax } from './_utilities';
import { dataTablePropTypes, ACTION_TYPES, ACTION_ICON_TYPES } from './_types';
import { ELEMENT_NAMES, CSS_CLASSES } from './_constants';
import { ExtendText } from '../../ExtendText/ExtendText';
import { formatForID } from '../../../../utils/strings/StringUtil';

import '@rmwc/data-table/styles';
import { SplitCSV } from '../../SplitCSV/SplitCSV';
import './DataTable.scss';

/**
 * Generic functional component for displaying datasets.
 * Supports row click, check, toggle, filtration, search, removal and validation.
 * @param {Object} props JSX attributes passed to component.
 * @returns {ReactElement} rendered component.
 */
const DataTable = props => {
	let {
		actions,
		areRowsLazyLoaded,
		children,
		className,
		errorMessage,
		filtered,
		filteredOptions,
		focusProps,
		headers,
		iconLegend,
		indexed,
		indexedHelperText,
		infiniteScroll,
		infiniteScrollThreshold,
		isReadOnly,
		label,
		loading,
		onChipSetChangedHandler,
		onLoadMoreResultsHandler,
		onRowClickedHandler,
		onTextFieldChangedHandler,
		pageSize,
		rows,
		sublabel,
		validation,
		resetSearchValue,
		searchfieldText,
		handleRightClick,
		withoutFormik = false,
		...propsCopy
	} = props;
	className = classnames('mdc-typography', CSS_CLASSES.ROOT, className);

	const childRef = useRef(null);

	/**
	 * Effect on initial render. Assigns scroll listener(s) to the containing element receiving table scroll events.
	 * Introduced by commit https://github.com/rmwc/rmwc/commit/d794eb5753c4d0dfaeaae3bd40e787822164aa02.
	 * @returns {Function} removes assigned listener(s) on clean-up.
	 */
	useEffect(() => {
		const arrElements = document.getElementsByClassName(
			'mdc-data-table__table-container'
		);
		Array.from(arrElements).forEach(element => {
			element?.addEventListener('scroll', onScrollEvent);
		});

		return () => {
			Array.from(arrElements).forEach(element => {
				element?.removeEventListener('scroll', onScrollEvent);
			});
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * Effect on local state change. Recalculates the number of action cells required for each row.
	 * All rows must have same number of action cell spaces regardless of whether row configured all spaces.
	 * @returns void. No clean-up performed.
	 */
	useEffect(() => {
		if (resetSearchValue !== null && resetSearchValue !== undefined) {
			childRef?.current?.setValue(resetSearchValue); // eslint-disable-line no-unused-expressions
		}
	}, [resetSearchValue]);

	const [sMaxActionCellCount, setMaxActionCellCount] = useState(
		getActionCellMax(rows)
	); // reference to number of action cells each row requires

	// used as a stepper for infinite scrolling
	const [pageStep, setPageStep] = useState(1);
	const [loadingMoreRows, setLoadingMoreRows] = useState(false);

	/**
	 * Effect on local state change. Recalculates the number of action cells required for each row.
	 * All rows must have same number of action cell spaces regardless of whether row configured all spaces.
	 * @returns void. No clean-up performed.
	 */
	useEffect(() => {
		let actionCellCount = getActionCellMax(rows);
		if (actionCellCount > sMaxActionCellCount) {
			setMaxActionCellCount(actionCellCount);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rows]);

	/**
	 * Helper function that enables both variants of infinite scroll.
	 *   1. Lazy Loading - using a paginated API and making use of specific props like
	 *      `onLoadMoreResultsHandler` & `areRowsLazyLoaded`. `onLoadMoreResultsHandler`
	 *      should be an async function that updates the `rows` prop after each fetch.
	 *
	 *   2. UI only - you already have all the data and just want to make use of infinite
	 *      scroll for UI performance.
	 *
	 * Required props per variant
	 *   1. Lazy Loading
	 *     - infiniteScroll
	 *     - areRowsLazyLoaded
	 *     - onLoadMoreResultsHandler
	 *     - pageSize
	 *
	 *   2. UI only
	 *     - infiniteScroll
	 *     - pageSize
	 *
	 * @summary Provides 2 options for powering the infinite scroll feature.
	 * @function
	 */
	const onScrollEvent = async e => {
		// set how close the scroll needs to be to the bottom of the container to trigger infinite scroll
		const scrollThreshold = infiniteScrollThreshold || 350;

		const scrollPositionInRelationToBottom =
			e.target.scrollHeight - e.target.scrollTop;
		const shouldTryToInfiniteScroll =
			infiniteScroll &&
			!!pageSize &&
			scrollPositionInRelationToBottom - e.target.clientHeight <=
				scrollThreshold;

		if (shouldTryToInfiniteScroll) {
			// Lazy Loading
			if (
				areRowsLazyLoaded &&
				!!onLoadMoreResultsHandler &&
				!loadingMoreRows
			) {
				setLoadingMoreRows(true);
				await onLoadMoreResultsHandler(pageStep + 1);
				setPageStep(step => step + 1);
				setLoadingMoreRows(false);
			} else {
				// UI only
				// we will increase our step counter whenever there are still rows that aren't displayed
				// and the user scrolls near the bottom of the container
				setPageStep(step => step + 1);
			}
		}
	};

	/**
	 * Creates an array of base action cells. Useful in filling rows configured
	 * with less than the maximum number of action cells.
	 * @summary Creates an array of base action cells.
	 * @function
	 * @param {Number} numActionCells number of filler action cells to create.
	 * @param {boolean} isHeader flag determining type of action cell used.
	 * @returns {Node[]} array of empty action cell elements.
	 */
	const getFillerActionCells = (numActionCells, isHeader = false) => {
		let fillerActionCells = [];
		let propsBase = {
			alignMiddle: true,
			hasFormControl: true,
		};
		for (let i = 0; i < numActionCells; i++) {
			const actionCell = isHeader ? (
				<DataTableHeadCell {...propsBase} key={nanoid()} />
			) : (
				<DataTableCell {...propsBase} key={nanoid()} />
			);
			fillerActionCells.push(actionCell);
		}
		return fillerActionCells;
	};

	/**
	 * Helper method for creating header action cell against prop `actions` indices.
	 * @param {Object} action nullable base object defining action.
	 * @returns {ReactElement} rendered elements.
	 */
	const getHeaderActionCell = (action = null) => {
		let headerActionCell = getFillerActionCells(1, true)[0];
		let headerActionCellControl = null;

		if (action && action.Header) {
			let { Checked, Key, onChangedHandler } = action.Header;
			if (onChangedHandler) {
				let isDisabled = action.disabled || loading || isReadOnly;
				switch (action.Type) {
					case ACTION_TYPES.CHECKBOX:
						headerActionCellControl = (
							<Checkbox
								sx={{ margin: '0px' }}
								inputProps={{
									id: `data-table${
										label && typeof label !== 'undefined'
											? `-${formatForID(label)}`
											: ''
									}__cell__header__checkbox`,
								}}
								disabled={isDisabled}
								checked={Checked}
								onChange={e => {
									onChangedHandler(e, rows);
								}}
								data-test={
									ELEMENT_NAMES.TABLE_ACTION_CELL_CONTROL
								}
							/>
						);
						break;
					case ACTION_TYPES.CANCEL:
						headerActionCellControl = (
							<IconButton
								disabled={isDisabled}
								onClick={e => onChangedHandler(e, rows)}
								data-test={
									ELEMENT_NAMES.TABLE_ACTION_CELL_CONTROL
								}
								id={`data-table${label && typeof label !== "undefined" ? `-${formatForID(label)}` : ''}__cell__header__cancel`}
							>
								<Icon icon={ACTION_ICON_TYPES.CANCEL} />
							</IconButton>
						);
						break;
					case ACTION_TYPES.DONE:
						headerActionCellControl = (
							<IconButton
								disabled={isDisabled}
								onClick={e => onChangedHandler(e, rows)}
								data-test={
									ELEMENT_NAMES.TABLE_ACTION_CELL_CONTROL
								}
								id={`data-table${label && typeof label !== "undefined" ? `-${formatForID(label)}` : ''}__cell__header__done`}
							>
								<Icon icon={ACTION_ICON_TYPES.DONE} />
							</IconButton>
						);
						break;
					case ACTION_TYPES.EDIT:
						headerActionCellControl = (
							<IconButton
								disabled={isDisabled}
								onClick={e => onChangedHandler(e, rows)}
								data-test={
									ELEMENT_NAMES.TABLE_ACTION_CELL_CONTROL
								}
								id={`data-table${label && typeof label !== "undefined" ? `-${formatForID(label)}` : ''}__cell__header__edit`}
							>
								<Icon icon={ACTION_ICON_TYPES.EDIT} />
							</IconButton>
						);
						break;
					case ACTION_TYPES.REMIND:
						headerActionCellControl = (
							<IconButton
								disabled={isDisabled}
								onClick={e => onChangedHandler(e, rows)}
								data-test={
									ELEMENT_NAMES.TABLE_ACTION_CELL_CONTROL
								}
								id={`data-table${label && typeof label !== "undefined" ? `-${formatForID(label)}` : ''}__cell__header__remind`}
							>
								<Icon icon={ACTION_ICON_TYPES.REMIND} />
							</IconButton>
						);
						break;
					case ACTION_TYPES.REMOVE:
						headerActionCellControl = (
							<IconButton
								disabled={
									(validation &&
										rows.length <=
											validation.minimumThreshold) ||
									isDisabled
								}
								onClick={e => onChangedHandler(e, rows)}
								data-test={
									ELEMENT_NAMES.TABLE_ACTION_CELL_CONTROL
								}
								id={`data-table${label && typeof label !== "undefined" ? `-${formatForID(label)}` : ''}__cell__header__remove`}
							>
								<Icon icon={ACTION_ICON_TYPES.DELETE} />
							</IconButton>
						);
						break;
					case ACTION_TYPES.REVERIFY:
						headerActionCellControl = (
							<IconButton
								disabled={isDisabled}
								onClick={e => onChangedHandler(e, rows)}
								data-test={
									ELEMENT_NAMES.TABLE_ACTION_CELL_CONTROL
								}
								id={`data-table${label && typeof label !== "undefined" ? `-${formatForID(label)}` : ''}__cell__header__reverify`}
							>
								<Icon icon={ACTION_ICON_TYPES.REVERIFY} />
							</IconButton>
						);
						break;
					case ACTION_TYPES.TOGGLE:
						headerActionCellControl = (
							<IconButton
								disabled={isDisabled}
								onClick={e => {
									const checked =
										e.target.innerText ===
										ACTION_ICON_TYPES.ADD
											? true
											: false;
									e.target.checked = checked;
									onChangedHandler(e, rows);
								}}
								data-test={
									ELEMENT_NAMES.TABLE_ACTION_CELL_CONTROL
								}
								id={`data-table${label && typeof label !== "undefined" ? `-${formatForID(label)}` : ''}__cell__header__toggle`}
							>
								<Icon
									icon={
										Checked
											? ACTION_ICON_TYPES.REMOVE
											: ACTION_ICON_TYPES.ADD
									}
								/>
							</IconButton>
						);
						break;
					case ACTION_TYPES.MODIFY_TOGGLE:
						headerActionCellControl = (
							<IconButton
								disabled={isDisabled}
								onClick={e => {
									const checked =
										e.target.innerText ===
										ACTION_ICON_TYPES.EDIT
											? true
											: false;
									e.target.checked = checked;
									onChangedHandler(e, rows);
								}}
								data-test={
									ELEMENT_NAMES.TABLE_ACTION_CELL_CONTROL
								}
								id={`data-table${label && typeof label !== "undefined" ? `-${formatForID(label)}` : ''}__cell__header__modify-toggle`}
							>
								<Icon
									icon={
										Checked
											? ACTION_ICON_TYPES.REMOVE
											: ACTION_ICON_TYPES.EDIT
									}
								/>
							</IconButton>
						);
						break;
					case ACTION_TYPES.REVOKE_TOGGLE:
						headerActionCellControl = (
							<IconButton
								disabled={isDisabled}
								onClick={e => {
									const checked =
										e.target.innerText ===
										ACTION_ICON_TYPES.DELETE
											? true
											: false;
									e.target.checked = checked;
									onChangedHandler(e, rows);
								}}
								data-test={
									ELEMENT_NAMES.TABLE_ACTION_CELL_CONTROL
								}
								id={`data-table${label && typeof label !== "undefined" ? `-${formatForID(label)}` : ''}__cell__header__revoke-toggle`}
							>
								<Icon
									icon={
										Checked
											? ACTION_ICON_TYPES.REMOVE
											: ACTION_ICON_TYPES.DELETE
									}
								/>
							</IconButton>
						);
						break;
					case ACTION_TYPES.REACTIVATE_TOGGLE:
						headerActionCellControl = (
							<IconButton
								disabled={isDisabled}
								onClick={e => {
									const checked =
										e.target.innerText ===
										ACTION_ICON_TYPES.PROBLEM
											? true
											: false;
									e.target.checked = checked;
									onChangedHandler(e, rows);
								}}
								data-test={
									ELEMENT_NAMES.TABLE_ACTION_CELL_CONTROL
								}
								id={`data-table${label && typeof label !== "undefined" ? `-${formatForID(label)}` : ''}__cell__header__reactivate-toggle`}
							>
								<Icon
									icon={
										Checked
											? ACTION_ICON_TYPES.REMOVE
											: ACTION_ICON_TYPES.PROBLEM
									}
								/>
							</IconButton>
						);
						break;
					default:
						break;
				}
				headerActionCell = (
					<DataTableHeadCell
						alignMiddle
						hasFormControl
						className={CSS_CLASSES.TABLE_ACTION_CELL}
						key={Key}
						data-test={ELEMENT_NAMES.TABLE_ACTION_CELL}
					>
						{headerActionCellControl}
					</DataTableHeadCell>
				);
			}
		}

		return headerActionCell;
	};

	/**
	 * Helper method for creating action cell against prop `rows[...].ActionCells`.
	 * @param {Object} row nullable table row containing action cell.
	 * @param {Object} actionIndex nullable index of base object defining action cell in `rows[...].ActionCells`.
	 * @returns {ReactElement} rendered elements.
	 */
	const getActionCell = (row = null, actionIndex = null) => {
		let actionCell = getFillerActionCells(1)[0];
		let actionCellControl = null;

		// ensure action cell existence
		if (
			row &&
			row.ActionCells &&
			row.ActionCells.length - 1 >= actionIndex
		) {
			let {
				BindTo,
				Checked,
				Key,
				Text = null,
				Disabled = false,
			} = row.ActionCells[actionIndex];

			// ensure bound action existence
			if (actions.length - 1 >= BindTo) {
				const boundAction = actions[BindTo];
				const callback = boundAction.onChangedHandler
					? e => {
							e.target.checked = !Checked;
							boundAction.onChangedHandler(e, rows, row);
					  }
					: null;

				if (callback) {
					let isDisabled =
						boundAction.disabled ||
						Disabled ||
						loading ||
						isReadOnly;
					switch (boundAction.Type) {
						case ACTION_TYPES.CHECKBOX:
							actionCellControl = (
								<Checkbox
									sx={{margin: '0px'}}
									inputProps={{
										id: `data-table${
											label &&
											typeof label !== 'undefined'
												? `-${formatForID(label)}`
												: ''
										}__option-${formatForID(
											Object.values(row.Cells)[0].Value
										)}__checkbox`,
									}}
									onChange={e => callback(e)}
									checked={Checked}
									disabled={isDisabled}
									data-test={
										ELEMENT_NAMES.TABLE_ACTION_CELL_CONTROL
									}
								/>
							);
							break;
						case ACTION_TYPES.CANCEL:
							actionCellControl = (
								<IconButton
									disabled={isDisabled}
									onClick={e =>
										boundAction.onChangedHandler(
											e,
											rows,
											row
										)
									}
									data-test={
										ELEMENT_NAMES.TABLE_ACTION_CELL_CONTROL
									}
									className={
										isDisabled
											? CSS_CLASSES.DISABLED
											: CSS_CLASSES.INVALID
									}
									id={`data-table${label && typeof label !== "undefined" ? `-${formatForID(label)}` : ''}__option-${formatForID(Object.values(row.Cells)[0].Value)}__cancel`}
								>
									<Icon icon={ACTION_ICON_TYPES.CANCEL} />
								</IconButton>
							);
							break;
						case ACTION_TYPES.DONE:
							actionCellControl = (
								<IconButton
									disabled={isDisabled}
									onClick={e =>
										boundAction.onChangedHandler(
											e,
											rows,
											row
										)
									}
									data-test={
										ELEMENT_NAMES.TABLE_ACTION_CELL_CONTROL
									}
									className={
										isDisabled
											? CSS_CLASSES.DISABLED
											: CSS_CLASSES.VALID
									}
									id={`data-table${label && typeof label !== "undefined" ? `-${formatForID(label)}` : ''}__option-${formatForID(Object.values(row.Cells)[0].Value)}__done`}
								>
									<Icon icon={ACTION_ICON_TYPES.DONE} />
								</IconButton>
							);
							break;
						case ACTION_TYPES.EDIT:
							actionCellControl = (
								<IconButton
									disabled={isDisabled}
									onClick={e =>
										boundAction.onChangedHandler(
											e,
											rows,
											row
										)
									}
									data-test={
										ELEMENT_NAMES.TABLE_ACTION_CELL_CONTROL
									}
									className={
										isDisabled
											? CSS_CLASSES.DISABLED
											: CSS_CLASSES.DEFAULT
									}
									id={`data-table${label && typeof label !== "undefined" ? `-${formatForID(label)}` : ''}__option-${formatForID(Object.values(row.Cells)[0].Value)}__edit`}
								>
									<Icon icon={ACTION_ICON_TYPES.EDIT} />
								</IconButton>
							);
							break;
						case ACTION_TYPES.REMIND:
							actionCellControl = (
								<IconButton
									disabled={isDisabled}
									onClick={e =>
										boundAction.onChangedHandler(
											e,
											rows,
											row
										)
									}
									data-test={
										ELEMENT_NAMES.TABLE_ACTION_CELL_CONTROL
									}
									className={
										isDisabled
											? CSS_CLASSES.DISABLED
											: CSS_CLASSES.DEFAULT
									}
									id={`data-table${label && typeof label !== "undefined" ? `-${formatForID(label)}` : ''}__option-${formatForID(Object.values(row.Cells)[0].Value)}__remind`}
								>
									<Icon icon={ACTION_ICON_TYPES.REMIND} />
								</IconButton>
							);
							break;
						case ACTION_TYPES.FLAG_BUSINESS:
							actionCellControl = (
								<IconButton
									disabled={
										(validation &&
											rows.length <=
												validation.minimumThreshold) ||
										isDisabled
									}
									onClick={e =>
										boundAction.onChangedHandler(
											e,
											rows,
											row
										)
									}
									data-test={
										ELEMENT_NAMES.TABLE_ACTION_CELL_CONTROL
									}
									className={CSS_CLASSES.INVALID}
									id={`data-table${label && typeof label !== "undefined" ? `-${formatForID(label)}` : ''}__option-${formatForID(Object.values(row.Cells)[0].Value)}__business`}
								>
									<Icon icon={ACTION_ICON_TYPES.BUSINESS} />
								</IconButton>
							);
							break;
						case ACTION_TYPES.FLAG_LOCK:
							actionCellControl = (
								<IconButton
									disabled={
										(validation &&
											rows.length <=
												validation.minimumThreshold) ||
										isDisabled
									}
									onClick={e =>
										boundAction.onChangedHandler(
											e,
											rows,
											row
										)
									}
									data-test={
										ELEMENT_NAMES.TABLE_ACTION_CELL_CONTROL
									}
									className={CSS_CLASSES.INVALID}
									id={`data-table${label && typeof label !== "undefined" ? `-${formatForID(label)}` : ''}__option-${formatForID(Object.values(row.Cells)[0].Value)}__lock`}
								>
									<Icon icon={ACTION_ICON_TYPES.LOCK} />
								</IconButton>
							);
							break;
						case ACTION_TYPES.FLAG_PERSON:
							actionCellControl = (
								<IconButton
									disabled={
										(validation &&
											rows.length <=
												validation.minimumThreshold) ||
										isDisabled
									}
									onClick={e =>
										boundAction.onChangedHandler(
											e,
											rows,
											row
										)
									}
									data-test={
										ELEMENT_NAMES.TABLE_ACTION_CELL_CONTROL
									}
									className={CSS_CLASSES.WARNING}
									id={`data-table${label && typeof label !== "undefined" ? `-${formatForID(label)}` : ''}__option-${formatForID(Object.values(row.Cells)[0].Value)}__person`}
								>
									<Icon icon={ACTION_ICON_TYPES.PERSON} />
								</IconButton>
							);
							break;
						case ACTION_TYPES.FLAG_TIME:
							actionCellControl = (
								<IconButton
									disabled={
										(validation &&
											rows.length <=
												validation.minimumThreshold) ||
										isDisabled
									}
									onClick={e =>
										boundAction.onChangedHandler(
											e,
											rows,
											row
										)
									}
									data-test={
										ELEMENT_NAMES.TABLE_ACTION_CELL_CONTROL
									}
									className={CSS_CLASSES.WARNING}
									id={`data-table${label && typeof label !== "undefined" ? `-${formatForID(label)}` : ''}__option-${formatForID(Object.values(row.Cells)[0].Value)}__schedule`}
								>
									<Icon icon={ACTION_ICON_TYPES.SCHEDULE} />
								</IconButton>
							);
							break;
						case ACTION_TYPES.FLAG_PROBLEM:
							actionCellControl = (
								<IconButton
									disabled={
										(validation &&
											rows.length <=
												validation.minimumThreshold) ||
										isDisabled
									}
									onClick={e =>
										boundAction.onChangedHandler(
											e,
											rows,
											row
										)
									}
									data-test={
										ELEMENT_NAMES.TABLE_ACTION_CELL_CONTROL
									}
									className={CSS_CLASSES.INVALID}
									id={`data-table${label && typeof label !== "undefined" ? `-${formatForID(label)}` : ''}__option-${formatForID(Object.values(row.Cells)[0].Value)}__problem`}
								>
									<Icon icon={ACTION_ICON_TYPES.PROBLEM} />
								</IconButton>
							);
							break;
						case ACTION_TYPES.FLAG_UPDATE:
							actionCellControl = (
								<IconButton
									disabled={
										(validation &&
											rows.length <=
												validation.minimumThreshold) ||
										isDisabled
									}
									onClick={e =>
										boundAction.onChangedHandler(
											e,
											rows,
											row
										)
									}
									data-test={
										ELEMENT_NAMES.TABLE_ACTION_CELL_CONTROL
									}
									className={CSS_CLASSES.WARNING}
									id={`data-table${label && typeof label !== "undefined" ? `-${formatForID(label)}` : ''}__option-${formatForID(Object.values(row.Cells)[0].Value)}__update`}
								>
									<Icon icon={ACTION_ICON_TYPES.UPDATE} />
								</IconButton>
							);
							break;
						case ACTION_TYPES.REMOVE:
							actionCellControl = (
								<IconButton
									disabled={
										(validation &&
											rows.length <=
												validation.minimumThreshold) ||
										isDisabled
									}
									onClick={e =>
										boundAction.onChangedHandler(
											e,
											rows,
											row
										)
									}
									data-test={
										ELEMENT_NAMES.TABLE_ACTION_CELL_CONTROL
									}
									id={`data-table${label && typeof label !== "undefined" ? `-${formatForID(label)}` : ''}__option-${formatForID(Object.values(row.Cells)[0].Value)}__delete`}
								>
									<Icon icon={ACTION_ICON_TYPES.DELETE} />
								</IconButton>
							);
							break;
						case ACTION_TYPES.REVERIFY:
							actionCellControl = (
								<IconButton
									disabled={isDisabled}
									onClick={e =>
										boundAction.onChangedHandler(
											e,
											rows,
											row
										)
									}
									data-test={
										ELEMENT_NAMES.TABLE_ACTION_CELL_CONTROL
									}
									className={CSS_CLASSES.VALID}
									id={`data-table${label && typeof label !== "undefined" ? `-${formatForID(label)}` : ''}__option-${formatForID(Object.values(row.Cells)[0].Value)}__reverify`}
								>
									<Icon icon={ACTION_ICON_TYPES.REVERIFY} />
								</IconButton>
							);
							break;
						case ACTION_TYPES.TOGGLE:
							actionCellControl = (
								<IconButton
									onClick={e => callback(e)}
									disabled={isDisabled}
									data-test={
										ELEMENT_NAMES.TABLE_ACTION_CELL_CONTROL
									}
									id={`data-table${label && typeof label !== "undefined" ? `-${formatForID(label)}` : ''}__option-${formatForID(Object.values(row.Cells)[0].Value)}__toggle`}
								>
									<Icon
										icon={
											Checked
												? ACTION_ICON_TYPES.REMOVE
												: ACTION_ICON_TYPES.ADD
										}
										
									/>
								</IconButton>
							);
							break;
						case ACTION_TYPES.MODIFY_TOGGLE:
							actionCellControl = (
								<IconButton
									onClick={e => callback(e)}
									disabled={isDisabled}
									data-test={
										ELEMENT_NAMES.TABLE_ACTION_CELL_CONTROL
									}
									id={`data-table${label && typeof label !== "undefined" ? `-${formatForID(label)}` : ''}__option-${formatForID(Object.values(row.Cells)[0].Value)}__modify-toggle`}
								>
									<Icon
										icon={
											Checked
												? ACTION_ICON_TYPES.REMOVE
												: ACTION_ICON_TYPES.EDIT
										}
										
									/>
								</IconButton>
							);
							break;
						case ACTION_TYPES.REVOKE_TOGGLE:
							actionCellControl = (
								<IconButton
									onClick={e => callback(e)}
									disabled={isDisabled}
									data-test={
										ELEMENT_NAMES.TABLE_ACTION_CELL_CONTROL
									}
									id={`data-table${label && typeof label !== "undefined" ? `-${formatForID(label)}` : ''}__option-${formatForID(Object.values(row.Cells)[0].Value)}__revoke-toggle`}
								>
									<Icon
										icon={
											Checked
												? ACTION_ICON_TYPES.REMOVE
												: ACTION_ICON_TYPES.DELETE
										}
										
									/>
								</IconButton>
							);
							break;
						case ACTION_TYPES.REACTIVATE_TOGGLE:
							actionCellControl = (
								<IconButton
									onClick={e => callback(e)}
									disabled={isDisabled}
									data-test={
										ELEMENT_NAMES.TABLE_ACTION_CELL_CONTROL
									}
									id={`data-table${label && typeof label !== "undefined" ? `-${formatForID(label)}` : ''}__option-${formatForID(Object.values(row.Cells)[0].Value)}__reactivate-toggle`}
								>
									<Icon
										icon={
											Checked
												? ACTION_ICON_TYPES.REMOVE
												: ACTION_ICON_TYPES.PROBLEM
										}
										
									/>
								</IconButton>
							);
							break;
						default:
							break;
					}
					if (Text) {
						actionCellControl = (
							<Tooltip
								align={CONTENT_ALIGNMENT.TOP}
								content={Text}
							>
								{actionCellControl}
							</Tooltip>
						);
					}
					actionCell = (
						<DataTableCell
							alignMiddle
							hasFormControl
							className={CSS_CLASSES.TABLE_ACTION_CELL}
							key={Key}
							data-test={ELEMENT_NAMES.TABLE_ACTION_CELL}
						>
							{actionCellControl}
						</DataTableCell>
					);
				}
			}
		}

		return actionCell;
	};

	let hasToolbar = filtered || indexed;

	// if we using infinite scroll for UI only, this will handle only showing N rows at a time
	const areStillRowsToShow = pageSize * pageStep <= rows.length;
	if (
		infiniteScroll &&
		!areRowsLazyLoaded &&
		!!pageSize &&
		areStillRowsToShow
	) {
		rows = rows.slice(0, pageSize * pageStep);
	}

	return (
		<StyleAuxiliary classes={CSS_CLASSES.ROOT_CONTAINER}>
			{label && (
				<Headline6>
					{label}
					{actions && iconLegend && (
						<Tooltip
							activateOn={'click'}
							className={CSS_CLASSES.ICON_LEGEND}
							content={
								<IconLegend
									icons={iconLegend.reduce(
										(arrIcons, icon) => {
											if (
												actions.length - 1 >=
												icon.BindTo
											) {
												switch (
													actions[icon.BindTo].Type
												) {
													case ACTION_TYPES.REMOVE:
														arrIcons.push({
															Key: ACTION_ICON_TYPES.DELETE,
															Value: icon.ValuePrimary,
														});
														break;
													case ACTION_TYPES.CANCEL:
														arrIcons.push({
															className:
																CSS_CLASSES.INVALID,
															Key: ACTION_ICON_TYPES.CANCEL,
															Value: icon.ValuePrimary,
														});
														break;
													case ACTION_TYPES.DONE:
														arrIcons.push({
															className:
																CSS_CLASSES.VALID,
															Key: ACTION_ICON_TYPES.DONE,
															Value: icon.ValuePrimary,
														});
														break;
													case ACTION_TYPES.EDIT:
														arrIcons.push({
															Key: ACTION_ICON_TYPES.EDIT,
															Value: icon.ValuePrimary,
														});
														break;
													case ACTION_TYPES.REMIND:
														arrIcons.push({
															Key: ACTION_ICON_TYPES.REMIND,
															Value: icon.ValuePrimary,
														});
														break;
													case ACTION_TYPES.REVERIFY:
														arrIcons.push({
															className:
																CSS_CLASSES.VALID,
															Key: ACTION_ICON_TYPES.REVERIFY,
															Value: icon.ValuePrimary,
														});
														break;
													case ACTION_TYPES.TOGGLE:
														arrIcons.push({
															Key: ACTION_ICON_TYPES.ADD,
															Value: icon.ValuePrimary,
														});
														if (
															icon.ValueSecondary
														) {
															arrIcons.push({
																Key: ACTION_ICON_TYPES.REMOVE,
																Value: icon.ValueSecondary,
															});
														}
														break;
													case ACTION_TYPES.MODIFY_TOGGLE:
														arrIcons.push({
															Key: ACTION_ICON_TYPES.EDIT,
															Value: icon.ValuePrimary,
														});
														if (
															icon.ValueSecondary
														) {
															arrIcons.push({
																Key: ACTION_ICON_TYPES.REMOVE,
																Value: icon.ValueSecondary,
															});
														}
														break;
													case ACTION_TYPES.REVOKE_TOGGLE:
														arrIcons.push({
															Key: ACTION_ICON_TYPES.DELETE,
															Value: icon.ValuePrimary,
														});
														if (
															icon.ValueSecondary
														) {
															arrIcons.push({
																Key: ACTION_ICON_TYPES.REMOVE,
																Value: icon.ValueSecondary,
															});
														}
														break;
													case ACTION_TYPES.REACTIVATE_TOGGLE:
														arrIcons.push({
															Key: ACTION_ICON_TYPES.PROBLEM,
															Value: icon.ValuePrimary,
														});
														if (
															icon.ValueSecondary
														) {
															arrIcons.push({
																Key: ACTION_ICON_TYPES.REMOVE,
																Value: icon.ValueSecondary,
															});
														}
														break;
													case ACTION_TYPES.FLAG_BUSINESS:
														arrIcons.push({
															className:
																CSS_CLASSES.INVALID,
															Key: ACTION_ICON_TYPES.BUSINESS,
															Value: icon.ValuePrimary,
														});
														break;
													case ACTION_TYPES.FLAG_LOCK:
														arrIcons.push({
															className:
																CSS_CLASSES.INVALID,
															Key: ACTION_ICON_TYPES.LOCK,
															Value: icon.ValuePrimary,
														});
														break;
													case ACTION_TYPES.FLAG_PERSON:
														arrIcons.push({
															className:
																CSS_CLASSES.WARNING,
															Key: ACTION_ICON_TYPES.PERSON,
															Value: icon.ValuePrimary,
														});
														break;
													case ACTION_TYPES.FLAG_TIME:
														arrIcons.push({
															className:
																CSS_CLASSES.WARNING,
															Key: ACTION_ICON_TYPES.SCHEDULE,
															Value: icon.ValuePrimary,
														});
														break;
													case ACTION_TYPES.FLAG_PROBLEM:
														arrIcons.push({
															className:
																CSS_CLASSES.INVALID,
															Key: ACTION_ICON_TYPES.PROBLEM,
															Value: icon.ValuePrimary,
														});
														break;
													case ACTION_TYPES.FLAG_UPDATE:
														arrIcons.push({
															className:
																CSS_CLASSES.WARNING,
															Key: ACTION_ICON_TYPES.UPDATE,
															Value: icon.ValuePrimary,
														});
														break;
													default:
														break;
												}
											}
											return arrIcons;
										},
										[],
									)}
								/>
							}
						>
							<Button
								variant='text'
								sx={{my: '0', mx:'15px'}}
								endIcon={<Icon icon='tour' />}
								id={`data-table${
									label && typeof label !== 'undefined'
										? `-${formatForID(label)}`
										: ''
								}__icon-legend__button`}
							>
								ICON HELP
							</Button>
						</Tooltip>
					)}
				</Headline6>
			)}
			{(sublabel || validation) && (
				<Subtitle2>
					{sublabel
						? sublabel
						: validation
							? 'Hover over invalid cells for more information.'
							: null}
				</Subtitle2>
			)}
			{errorMessage && (
				<Subtitle2 className={CSS_CLASSES.INVALID}>
					{errorMessage}
				</Subtitle2>
			)}
			{hasToolbar ? (
				<StyleAuxiliary
					classes={classnames(CSS_CLASSES.TOOLBAR, {
						[`${CSS_CLASSES.MULTI_TOOL_TOOLBAR}`]:
							indexed && filtered,
					})}
				>
					{indexed && (
						<TextField
							ref={childRef}
							help={indexedHelperText}
							inputProps={{
								id: `data-table${
									label && typeof label !== 'undefined'
										? `-${formatForID(label)}`
										: ''
								}__tool-bar__text-field__search`,
							}}
							canClear
							className={CSS_CLASSES.TOOLBAR_TEXT_FIELD}
							label={searchfieldText ?? ''}
							leadingIcon={<Icon icon='search' />}
							onChange={onTextFieldChangedHandler}
							outlined
							trailingIcon={<Icon icon='' />}
							data-test={ELEMENT_NAMES.TOOLBAR_TEXT_FIELD}
							withoutFormik={withoutFormik}
						/>
					)}
					{filtered && (
						<StyleAuxiliary
							classes={classnames(
								CSS_CLASSES.TOOLBAR_CHIP_SET_CONTAINER,
							)}
						>
							<Icon icon='filter_list' />
							<ChipSet
								className={CSS_CLASSES.TOOLBAR_CHIP_SET}
								filter
								handleSelect={onChipSetChangedHandler}
								data-test={ELEMENT_NAMES.TOOLBAR_CHIP_SET}
							>
								{filteredOptions &&
									filteredOptions.map(option => {
										const optionLower =
											option.toLowerCase();
										return (
											<Chip
												id={`data-table${
													label &&
													typeof label !== 'undefined'
														? `-${formatForID(
																label,
															)}`
														: ''
												}__tool-bar__filter-${optionLower}`}
												key={`option_${optionLower}`}
												label={option}
												data-test={
													ELEMENT_NAMES.TOOLBAR_CHIP_SET_CHIP
												}
												disabled={isReadOnly}
											/>
										);
									})}
							</ChipSet>
						</StyleAuxiliary>
					)}
				</StyleAuxiliary>
			) : null}
			<StyleAuxiliary classes={CSS_CLASSES.TABLE_CONTAINER}>
				{loading && (
					<LinearProgress
						sx={{ width: '100%' }}
						color='primary'
						className={classnames({
							[`${CSS_CLASSES.PROGRESS_BAR}`]: !hasToolbar,
							[`${CSS_CLASSES.HIDDEN_PROGRESS_BAR}`]: !loading,
							[`${CSS_CLASSES.SUCCEEDING_PROGRESS_BAR}`]:
								hasToolbar,
						})}
					/>
				)}
				<RMWCDataTable
					className={classnames(className, {
						[`${CSS_CLASSES.SUCCEEDING_TABLE}`]:
							hasToolbar || loading,
					})}
					onScroll={onScrollEvent}
					{...propsCopy}
				>
					<DataTableContent data-test={ELEMENT_NAMES.ROOT}>
						<DataTableHead data-test={ELEMENT_NAMES.TABLE_HEAD}>
							<DataTableRow>
								{[...new Array(sMaxActionCellCount)].map(
									(_, i) =>
										getHeaderActionCell(
											actions && actions.length - 1 >= i
												? actions[i]
												: null,
										),
								)}
								{validation && (
									<DataTableHeadCell
										alignMiddle
										hasFormControl
										className={
											CSS_CLASSES.TABLE_HEADER_CELL
										}
									>
										Validation State
									</DataTableHeadCell>
								)}
								{headers &&
									headers.map((header, headerIndex) => (
										<DataTableHeadCell
											{...header.props}
											key={headerIndex}
											className={classnames(
												CSS_CLASSES.TABLE_HEADER_CELL,
												{
													[`${CSS_CLASSES.INTERACTIVE_TABLE_HEADER_CELL}`]:
														header.props &&
														header.props
															.onSortChange,
												},
											)}
											data-test={
												ELEMENT_NAMES.TABLE_HEADER_CELL
											}
										>
											{header.Value}
										</DataTableHeadCell>
									))}
							</DataTableRow>
						</DataTableHead>
						<DataTableBody
							className={classnames({
								[`${CSS_CLASSES.LOADING_TABLE_BODY}`]:
									loading || isReadOnly,
							})}
							data-test={ELEMENT_NAMES.TABLE_BODY}
						>
							{rows &&
								rows.map((row, rowIndex) => {
									let isRowValid = validation
										? Object.keys(row.Cells).every(
												key =>
													row.Cells[
														key
													].Validation.Key.toLowerCase() ===
													'true',
											)
										: null;
									// getActionCell() accounts for invalid indices
									const actionCells = [
										...new Array(sMaxActionCellCount),
									].map((_, i) => getActionCell(row, i));

									const { hideValidationIcon, ...rowProps } =
										row.props || {};

									//show validation cell if `validation` object is present and if row's `hideValidationIcon` property is not set
									let validationCell = validation ? (
										hideValidationIcon !== true ? (
											<DataTableCell
												alignMiddle
												className={classnames(
													CSS_CLASSES.TABLE_ICON_CELL,
												)}
											>
												<Icon
													icon={
														isRowValid
															? 'check_circle_outline'
															: 'block'
													}
													className={classnames({
														[`${CSS_CLASSES.VALID}`]:
															isRowValid,
														[`${CSS_CLASSES.INVALID}`]:
															!isRowValid,
													})}
													title={
														isRowValid
															? 'Valid'
															: 'Invalid'
													}
													aria-label={
														isRowValid
															? 'Valid'
															: 'Invalid'
													}
												/>
											</DataTableCell>
										) : (
											<DataTableCell
												alignMiddle
												className={classnames(
													CSS_CLASSES.TABLE_ICON_CELL,
												)}
											></DataTableCell>
										)
									) : null;
									let cells = (
										<Fragment>
											{headers.map(header => {
												const arrRowKeys = Object.keys(
													row.Cells,
												);
												const key = arrRowKeys.find(
													key =>
														header.Value ===
														key
															.trim()
															.replace(/_/g, ' '),
												);
												const keyIndex =
													arrRowKeys.indexOf(key);
												const cellObject =
													row.Cells[key];
												const isCellEditable =
													cellObject.isEditable ||
													false;
												const isCellValid =
													cellObject.Validation.Key.toLowerCase() ===
													'true';
												const errorMessage =
													!isCellValid &&
													cellObject.Validation.Value
														? cellObject.Validation
																.Value
																.ErrorMessage
														: null;
												const editableField = (
													<StyleAuxiliary
														classes={classnames({
															'has-error':
																!isCellValid,
														})}
													>
														<input
															type='text'
															className={classnames(
																'form-control',
																{
																	[`${CSS_CLASSES.INVALID}`]:
																		!isCellValid &&
																		cellObject.Value,
																},
															)}
															disabled={
																loading ||
																isReadOnly
															}
															placeholder='Enter value'
															value={
																rows[rowIndex]
																	.Cells[key]
																	.Value
															}
															onChange={e =>
																validation.onCellChangedHandler(
																	e,
																	row,
																	key,
																)
															}
														/>
													</StyleAuxiliary>
												);

												return (
													<DataTableCell
														key={keyIndex}
														alignMiddle={
															!cellObject.Value
														}
														aria-label={
															errorMessage
														}
														title={errorMessage}
														{...cellObject.props}
														data-test={
															ELEMENT_NAMES.TABLE_BODY_CELL
														}
													>
														{isCellValid ? (
															isCellEditable ? (
																editableField
															) : cellObject.Value ? (
																cellObject
																	?.props
																	?.extendtext ? (
																	<ExtendText
																		text={
																			cellObject.Value
																		}
																		maxLength={
																			cellObject
																				.props
																				.extendtext
																		}
																		textClickHandler={
																			cellObject
																				?.props
																				?.cellTextOnClick
																		}
																	/>
																) : cellObject
																		?.props
																		?.breakText ? (
																	<SplitCSV
																		text={
																			cellObject.Value
																		}
																	/>
																) : cellObject
																		?.props
																		?.leadingIcon ? (
																	<>
																		{
																			cellObject
																				.props
																				.leadingIcon
																		}
																		{
																			cellObject.Value
																		}
																	</>
																) : (
																	cellObject.Value
																)
															) : (
																'\u2014'
															)
														) : (
															editableField
														)}
													</DataTableCell>
												);
											})}
										</Fragment>
									);
									return (
										<DataTableRow
											onContextMenu={event =>
												handleRightClick?.(row, event)
											}
											key={row.Key}
											{...rowProps}
											onClick={
												onRowClickedHandler &&
												!row.ActionCells
													? () =>
															onRowClickedHandler(
																row,
															)
													: null
											}
											data-test={
												ELEMENT_NAMES.TABLE_BODY_ROW
											}
											id={`data-table${
												label &&
												typeof label !== 'undefined'
													? `-${formatForID(label)}`
													: ''
											}__row-${formatForID(
												Object.values(row.Cells)[0]
													.Value,
											)}`}
										>
											{actionCells}
											{validationCell}
											{cells}
										</DataTableRow>
									);
								})}
						</DataTableBody>
						{children}
					</DataTableContent>
				</RMWCDataTable>
			</StyleAuxiliary>
		</StyleAuxiliary>
	);
};

DataTable.propTypes = dataTablePropTypes;

export {
	DataTable,
	SimpleDataTable,
	DataTableContent,
	DataTableHead,
	DataTableHeadCell,
	DataTableBody,
	DataTableRow,
	DataTableCell,
};
